import { forModel } from "@eusoft/webapp-jsx";
import { Image, Action, Content, contentInfo, formatCurrency, formatDate, formatText, formatTextSimple, MaterialIcon, Popup, type IStateContext, type IStateManager, formatEnum } from "@eusoft/webapp-ui";
import { paymentService } from "../../abstraction/IPaymentService";
import { printAsync } from "../../actions/Common";
import { Card } from "../../components/Card";
import { Field } from "../../components/Field";
import { StatusTag } from "../../components/StatusTag";
import { TransactionList } from "../../components/TransactionList";
import {  PaymentCardType, SortDirection,  type Guid, type IPaymentCardInfo, type ITransactionView } from "../../entities/Commands";
import { formatCardStatus } from "../../helpers/Format";
import { Authorize } from "../../services/Authorize";
import { context } from "../../services/Context";
import { executeAsync } from "../../services/DataService";
import { WithContext } from "../../services/WithContext";
import "./CardDetailsPage.scss";
import { fullUri } from "@eusoft/webapp-framework/helpers/Utils";
import { apiClient } from "../../services/PmApiClient";
import { isNativeApp } from "../../services/NativePayment";
import { registerComponent } from "@eusoft/webapp-core/Component";

export interface ICardDetailsArgs {
    cardCode?: string;
    walletId?: Guid;
}

export class CardDetailsPage extends Content<ICardDetailsArgs> implements IStateManager {

    constructor() {

        super();

        this.init(CardDetailsPage, {
            title: "card-details",
            style: ["panel"],            
            body: forModel(this, m => <>

                <div className="balance">{formatCurrency(m.cardInfo.balance)}</div>
                <Card style="fieldset">
                    <Field label="card-number">{m.code}</Field>
                    <Field label="sale-circuit">{m.cardInfo.saleCircuit?.name}</Field>
                    <Field label="type">{formatEnum(PaymentCardType, m.cardInfo.type)}</Field>
                    <Field label="owner">{m.cardInfo.customer?.name}</Field>
                    <Field label="email">{m.cardInfo.customer?.email}</Field>
                    <Field label="phone-number">{m.cardInfo.customer?.mobileNumber}</Field>
                    <Field label="emitted-on">{formatDate(m.cardInfo.emissionTime, formatTextSimple("format-date-time") as string)}</Field>
                    <Field label="emitted-in">{m.cardInfo.emissionSalePoint?.name}</Field>
                    <Field label="blocked-on">{formatDate(m.cardInfo.blockTime, formatTextSimple("format-date-time") as string)}</Field>
                    <Field label="last-operation">{!m.cardInfo.blockTime && formatDate(m.cardInfo.lastOperationTime, formatTextSimple("format-date-time") as string)}</Field>
                    <Field label="card-state">
                        <StatusTag {...formatCardStatus(m.cardInfo.cardStatus)}/>
                    </Field> 
                    <div className="row card-actions">
                        <Action style="text" onExecuteAsync={() => m.showQrAsync()}>
                            qr-code
                        </Action>
                        {isNativeApp() && <Action style="text" onExecuteAsync={() => m.printAsync()}>
                            print-receipt
                        </Action>}
                    </div>
          
                </Card>
                {m.transactions && <TransactionList transactions={m.transactions} />}
                <Action type="global" onExecuteAsync={m.checkAsync}>
                    check-new
                </Action>
            </>)            
        });
    }

    async printAsync() {

        await printAsync("InitCard", {
            walletId: this.cardInfo.walletId,
            cardCode: this.code
        })
    }

    async showQrAsync() {

        const uri = fullUri("/wallet/" + this.cardInfo.walletId);

        const blob = await apiClient.qrCodeAsync(uri);

        const popup = new Popup({
            body: forModel(this, m => <>
                <Image>{blob}</Image>
            </>),
            name: "delegate",
            title: formatText("qr-code"),
            actions: [{
                name: "close",
                text: formatText("close") as string,
                executeAsync: async () => true,
            }]
        });

        popup.showAsync();
    }

    override async onLoadAsync(args: ICardDetailsArgs) {

        if (!args.cardCode) {
            const card = await paymentService.readCardAsync({
                message: "msg-wait-for-card-verify"
            });
            if (!card?.code)
                return false;
            args.cardCode = card.code;
        }

        this.code = args.cardCode;

        this.cardInfo = await executeAsync("GetCardInfo", { cardCode: args.cardCode });

        if (this.cardInfo?.walletId) {

            this.transactions = (await executeAsync("ListTransactions", {
                fromWalletId: this.cardInfo.walletId,
                toWalletId: this.cardInfo.walletId,
                saleCircuitId: context.saleCircuit.id,
                sort: {
                    direction: SortDirection.Descending,
                    property: "Time"
                },
                pagination: {
                    limit: 10,
                    offset: 0
                }
            }))?.items;

        }
        else
            this.transactions = null;

        return true;
    }

    restoreState(container: Record<string, unknown>, ctx: IStateContext) {

        this.code = container?.code as string;   
    }

    saveState(container: Record<string, unknown>, ctx: IStateContext) {

        container.code = this.code;
    }


    async checkAsync() {

        await this.onLoadAsync({});
    }

    cardInfo: IPaymentCardInfo;

    code: string;

    transactions: ITransactionView[];


    static override info = contentInfo({
        name: "card-details",
        route: "/cards/{cardCode}",
        icon: <MaterialIcon style="blue" name="paid" />,
        factory: () => new CardDetailsPage(),
        features: [
            Authorize(a => a
                .businessAccount()
                .resource("PaymentCard", "read")
            ),
            WithContext("sale-circuit")
        ]
    });
}

registerComponent(CardDetailsPage, "CardDetailsPage");